import React, { FC, useState, useEffect, useRef } from "react";
import {
  Container,
  HeadWrapper,
  BodyModal,
  Icon,
  Divider,
  CheckBoxWrapper,
  RolesContainer,
} from './styles'
import { icons } from "../../../icons";
import Typography from "../../typography";
import Button from "../../button";
import { useTranslation } from "react-i18next";
import Select from "../../select";
import { HeadLine3 } from "../../typography/styled";
import CheckBox from "../../checkbox";
import { clientService } from "../../../api";
import { useUi } from "../../../context/ui.context";

type ModalProps = {
  closeModalCancel: () => void;
  closeModal: () => void;
  alertTitle?: string;
  viewModal?: boolean;
  dataToEdit?: any;
}

const AssociateClient: FC<ModalProps> = (props) => {
  const {
    closeModalCancel,
    closeModal,
    alertTitle,
    viewModal,
    dataToEdit,
  } = props
  const [t] = useTranslation()
  const { viewAlert } = useUi()

  const userId = dataToEdit?.userId

  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)

  // Clients list and search
  const [clients, setClients] = useState<any[]>([])
  const [clientsLoading, setClientsLoading] = useState<boolean>(false)
  const [clientSearchString, setClientSearchString] = useState<string>('')
  const [selectedClientId, setSelectedClientId] = useState<any>(null)

  // Roles
  const [availableRoles, setAvailableRoles] = useState<any[]>([])
  const [selectedRoles, setSelectedRoles] = useState<number[]>([])

  const isLoadingRef = useRef(false)

  // Load available roles when a client is selected
  useEffect(() => {
    if (selectedClientId && viewModal) {
      clientService.getAvailableRoles(selectedClientId)
        .then((res) => {
          setAvailableRoles(res.data.roles || [])
          if (res.data.default_role_id) {
            setSelectedRoles([res.data.default_role_id])
          }
        })
        .catch((e) => {
          console.error('Failed to load roles', e)
        })
    }
  }, [selectedClientId, viewModal])

  // Load clients with search only when modal is open
  useEffect(() => {
    if (!viewModal || !userId || isLoadingRef.current) return

    isLoadingRef.current = true
    setClientsLoading(true)

    clientService.getClientsForAssociation({
      page: 1,
      page_size: 100,
      search: clientSearchString,
      filtering: `users__id__ne:${userId}`
    })
      .then((res) => {
        const clientsData = res.data?.objects || []
        const formattedClients = clientsData.map((client: any) => ({
          id: client.id,
          value: client.id,
          label: client.name || client.display || `Client #${client.id}`,
          data: client
        }))
        setClients(formattedClients)
      })
      .catch((error) => {
        console.error("Failed to load clients", error)
      })
      .finally(() => {
        setClientsLoading(false)
        isLoadingRef.current = false
      })
  }, [clientSearchString, userId, viewModal])

  // Reset state when modal closes
  useEffect(() => {
    if (!viewModal) {
      setSelectedClientId(null)
      setClientSearchString('')
      setSelectedRoles([])
      setClients([])
      setAvailableRoles([])
    }
  }, [viewModal])

  const editSelectedRoles = (id: number) => {
    const founded = selectedRoles.find((item: number) => item === id)
    if (founded) {
      setSelectedRoles((prev: number[]) => prev.filter((item: number) => item !== id))
    } else {
      setSelectedRoles([...selectedRoles, id])
    }
  }

  const handleAssociate = () => {
    if (!selectedClientId || !userId) {
      viewAlert({
        severity: 'error',
        message: t("SELECT_CLIENT_REQUIRED")
      })
      return
    }

    setLoading(true)
    clientService.associateClientToUser(userId, selectedClientId, selectedRoles)
      .then(() => {
        setLoadingComplete(true)
        viewAlert({
          severity: 'info',
          message: t("CLIENT_ASSOCIATED_SUCCESS")
        })
        setTimeout(() => {
          setLoadingComplete(false)
          setLoading(false)
          closeModal()
        }, 800)
      })
      .catch((e) => {
        setLoading(false)
        setLoadingComplete(false)
        if (e?.response?.data?.detail) {
          viewAlert({
            severity: 'error',
            message: e?.response?.data?.detail
          })
        }
      })
  }

  return (
    <Container>
      <HeadWrapper>
        <HeadLine3 style={{ marginTop: '10px', marginLeft: '10px' }}>
          {alertTitle || t("ASSOCIATE_CLIENT")}
        </HeadLine3>
        <Icon onClick={closeModalCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        <Select
          required
          toolTip={false}
          data={clients}
          selectedValue={selectedClientId}
          onChange={(id: any) => setSelectedClientId(id)}
          title={t("SELECT_CLIENT")}
          placeholder={t("SELECT_CLIENT")}
          fieldName='client'
          hasSearch={true}
          onSearchChange={setClientSearchString}
          errors={{}}
          touched={{ client: true }}
        />

        {selectedClientId && availableRoles.length > 0 && (
          <>
            <Divider />
            <Typography variant='body-medium'>
              {t("SELECT_USER_ROLES")}
            </Typography>
            <RolesContainer>
              {availableRoles.map((item: any) => (
                <CheckBoxWrapper key={item.id} onClick={() => editSelectedRoles(item.id)}>
                  <CheckBox
                    title={item.display_name || item.name}
                    value={selectedRoles.includes(item.id)}
                    onChange={() => editSelectedRoles(item.id)}
                  />
                </CheckBoxWrapper>
              ))}
            </RolesContainer>
          </>
        )}

        <div style={{ display: 'flex', gap: '6px', marginTop: '30px' }}>
          <Button
            variant="stroke"
            size="display"
            customStyles={{ width: '177px', justifyContent: 'center' }}
            onClick={closeModalCancel}
            title={t("CANCEL")}
          />
          <Button
            variant="primary"
            size="display"
            loading={loading}
            loadingComplete={loadingComplete}
            disabled={!selectedClientId}
            customStyles={{ width: '177px', justifyContent: 'center' }}
            onClick={handleAssociate}
            title={t("ASSOCIATE_CLIENT")}
          />
        </div>
      </BodyModal>
    </Container>
  )
}

export default AssociateClient
